import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, ShieldAlert, Link2, Linkedin, Award, CalendarDays, ArrowUpRight, FileText } from "lucide-react";
import { downloadFile } from "@/components/admin/BulkUpload";
import { ContactDialog } from "@/components/ContactDialog";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AppearanceMenu } from "@/components/AppearanceMenu";
import { Logo } from "@/components/Logo";
import { Loading } from "@/components/States";
import { BadgeMedal } from "@/components/BadgeMedal";
import { awardedDate } from "@/pages/Badges";
import { showcaseUrl } from "@/components/ShowcaseSettings";
import { Avatar } from "@/components/ShowcaseExtras";
import { api, fetchSubjects } from "@/lib/api";

const ORIGIN = process.env.REACT_APP_BACKEND_URL;

const fmt = (s) => awardedDate(s).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });

function CertCard({ b }) {
  return (
    <Link to={`/badge/${b.id}`} data-testid={`showcase-cert-${b.id}`} className="card-hover group flex items-start gap-4 rounded-2xl border border-line bg-surface p-5 text-left">
      <BadgeMedal badge={b} />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-aqua">{b.kind === "course" ? "Course certificate" : "Mastery certificate"}</p>
        <h3 className="mt-1 font-bold text-ink">{b.title}</h3>
        <p className="mt-1 text-sm text-ink-2">{b.description}</p>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1 font-medium text-emerald-700"><Check className="h-3.5 w-3.5" strokeWidth={3} />Verified</span>
          <span className="text-ink-3">{b.kind === "course" ? "Completed" : "Awarded"} {fmt(b.awarded_at)}</span>
        </div>
      </div>
      <ArrowUpRight className="h-4 w-4 shrink-0 text-ink-3 transition-colors group-hover:text-aqua" />
    </Link>
  );
}

function Achievements({ items }) {
  if (!items.length) return null;
  return (
    <div data-testid="showcase-achievements" className="mt-10">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-[0.15em] text-ink-3">Achievements</h2>
      <div className="flex flex-wrap gap-2">
        {items.map((b) => (
          <Link key={b.id} to={`/badge/${b.id}`} data-testid={`showcase-achievement-${b.id}`} className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-medium text-orange-800 hover:bg-orange-100">{b.title}</Link>
        ))}
      </div>
    </div>
  );
}

function Header({ p }) {
  const url = showcaseUrl(p.handle);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Profile link copied"); } catch (_) { toast.info(url); }
  };
  return (
    <div className="flex flex-col gap-5 border-b border-line pb-8 sm:flex-row sm:items-end sm:justify-between">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
      <Avatar testId="showcase-photo" name={p.name} photoUrl={p.photo_url ? `${ORIGIN}${p.photo_url}` : null} size="h-28 w-28 text-3xl" />
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-aqua">IDDCR LearnAI · Certificate showcase</p>
        <h1 data-testid="showcase-name" className="mt-3 text-4xl font-extrabold text-ink sm:text-5xl">{p.name}</h1>
        {p.headline && <p data-testid="showcase-headline" className="mt-2 text-lg text-ink-2">{p.headline}</p>}
        {p.bio && <p data-testid="showcase-bio" className="mt-3 max-w-xl whitespace-pre-line text-sm leading-relaxed text-ink-2">{p.bio}</p>}
        <div className="mt-4 flex flex-wrap gap-4 text-sm text-ink-3">
          <span data-testid="showcase-total" className="inline-flex items-center gap-1.5"><Award className="h-4 w-4 text-aqua" /><b className="text-ink">{p.total_certificates}</b> verified certificate{p.total_certificates === 1 ? "" : "s"}</span>
          {p.member_since && <span data-testid="showcase-member-since" className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-aqua" />Member since {new Date(p.member_since).toLocaleDateString(undefined, { month: "long", year: "numeric" })}</span>}
        </div>
      </div>
      </div>
      <div className="flex shrink-0 flex-wrap gap-2 sm:max-w-xs sm:justify-end">
        {p.contact_enabled && <ContactDialog handle={p.handle} name={p.name} />}
        <Button size="sm" variant="outline" data-testid="showcase-cv-btn" onClick={() => downloadFile(`/public/learners/${p.handle}/cv`, `${p.handle}-iddcr-cv.pdf`)} className="rounded-full border-brand text-ink"><FileText className="mr-1.5 h-3.5 w-3.5" />Download CV</Button>
        <Button size="sm" variant="outline" data-testid="showcase-copy-link" onClick={copy} className="rounded-full"><Link2 className="mr-1.5 h-3.5 w-3.5" />Copy link</Button>
        <Button size="sm" asChild className="rounded-full bg-[#0A66C2] hover:bg-[#08539e]">
          <a data-testid="showcase-linkedin" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer"><Linkedin className="mr-1.5 h-3.5 w-3.5" />Share on LinkedIn</a>
        </Button>
      </div>
    </div>
  );
}

export default function LearnerPublic() {
  const { handle } = useParams();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    setP(null); setMissing(false);
    Promise.all([api.get(`/public/learners/${encodeURIComponent(handle)}`), fetchSubjects()])
      .then(([r, subjects]) => {
        const icon = (b) => ({ ...b, icon: subjects.find((s) => s.id === b.subject_id)?.icon });
        setP({ ...r.data, certificates: r.data.certificates.map(icon), achievements: r.data.achievements });
      })
      .catch(() => setMissing(true));
  }, [handle]);

  return (
    <div className="grid-bg min-h-screen bg-canvas px-4 py-10">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between"><Link to="/"><Logo className="h-10" /></Link><AppearanceMenu /></div>
        <div className="mt-12">
          {missing ? (
            <div data-testid="showcase-missing" className="flex flex-col items-start gap-3 rounded-3xl border border-line bg-surface p-10">
              <ShieldAlert className="h-8 w-8 text-ink-3" />
              <p className="font-bold text-ink">Profile not available</p>
              <p className="text-sm text-ink-2">This showcase doesn't exist or the learner has made it private.</p>
            </div>
          ) : !p ? <Loading /> : (
            <div data-testid="showcase-page" className="fade-up">
              <Header p={p} />
              <h2 className="mb-4 mt-10 text-sm font-semibold uppercase tracking-[0.15em] text-ink-3">Verified certificates</h2>
              {p.certificates.length === 0 ? (
                <p data-testid="showcase-empty" className="rounded-2xl border border-dashed border-line-strong bg-canvas-2/60 p-8 text-sm text-ink-2">No certificates yet. This learner is working towards their first IDDCR certificate.</p>
              ) : (
                <div data-testid="showcase-certs" className="grid grid-cols-1 gap-4 md:grid-cols-2">{p.certificates.map((b) => <CertCard key={b.id} b={b} />)}</div>
              )}
              <Achievements items={p.achievements} />
              <p className="mt-12 text-xs text-ink-3">Each certificate links to its live verification page issued by {p.issuer}.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
